import React, { useEffect, useState, useRef } from "react";
import { ArrowRight, Upload, Sparkles, Check, AlertCircle, Undo2 } from "lucide-react";
import { Page, Card, Pill, PrimaryButton } from "../components/UI";
import api from "../lib/api";
import { toast } from "sonner";

const STEPS = ["Welcome", "Choose Source", "Connect / Upload", "Map Fields", "Run", "Review"];

export default function Merge() {
  const [step, setStep] = useState(0);
  const [pathway, setPathway] = useState(""); // software | paper | spreadsheet
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/merge/sources").then(({ data }) => setSources(data));
    api.get("/merge/migrations").then(({ data }) => setMigrations(data));
  }, []);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return toast.error("Choose a CSV file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("source", selectedSource?.id || "csv");
    try {
      const { data } = await api.post("/merge/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.auto_mapping);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not parse file"); }
  };

  const run = async () => {
    setRunning(true);
    setStep(4);
    try {
      const { data } = await api.post("/merge/run", {
        source: selectedSource?.id || "csv",
        file_b64: preview.file_b64,
        mapping,
        skip_duplicates: true,
      });
      setResult(data);
      const fresh = await api.get("/merge/migrations");
      setMigrations(fresh.data);
      setStep(5);
      toast.success(`Imported ${data.imported} clients`);
    } catch { toast.error("Migration failed"); }
    setRunning(false);
  };

  const rollback = async (id) => {
    if (!window.confirm("Rollback this migration? All imported clients will be deleted.")) return;
    try {
      const { data } = await api.post(`/merge/rollback/${id}`);
      toast.success(`Rolled back ${data.deleted} records`);
      const fresh = await api.get("/merge/migrations");
      setMigrations(fresh.data);
    } catch { toast.error("Rollback failed"); }
  };

  return (
    <Page title="Clinician Well Merge" subtitle="A calm, guided way to bring your practice home.">
      <Card delay={0.05} className="mb-5" testid="merge-progress">
        <div className="flex items-center gap-2 mb-3 overflow-x-auto">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 whitespace-nowrap">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${i < step ? "" : i === step ? "" : ""}`} style={{ background: i <= step ? "var(--mf-primary)" : "var(--mf-muted)", color: i <= step ? "#FAFAF8" : "var(--mf-text-muted)" }}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span className={`text-xs ${i === step ? "font-medium" : ""}`} style={{ color: i === step ? "var(--mf-primary)" : "var(--mf-text-muted)" }}>{s}</span>
              {i < STEPS.length - 1 && <ArrowRight className="w-3 h-3 mx-1" style={{ color: "var(--mf-text-muted)" }} />}
            </div>
          ))}
        </div>
        <div className="text-xs flex items-center gap-1" style={{ color: "var(--mf-secondary)" }}>
          <Sparkles className="w-3 h-3" /> Your data is encrypted and safe. Rollback available for 30 days.
        </div>
      </Card>

      {step === 0 && (
        <Card testid="merge-welcome" delay={0.1}>
          <h2 className="font-serif text-3xl mb-2">Let's bring your practice home.</h2>
          <p className="text-sm mb-5" style={{ color: "var(--mf-text-muted)" }}>How does your data live today?</p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { id: "software", label: "Another EHR", desc: "SimplePractice, TherapyNotes, Jane, Kareo, Epic, etc.", icon: "📦" },
              { id: "paper", label: "Paper Charts", desc: "Filing cabinets, handwritten notes, manilla folders.", icon: "📁" },
              { id: "spreadsheet", label: "Forms & Spreadsheets", desc: "Google Forms, Sheets, Excel, CSV.", icon: "📊" },
            ].map((p) => (
              <button key={p.id} onClick={() => { setPathway(p.id); setStep(1); }} className="p-6 rounded-2xl text-left transition-all hover:-translate-y-1" style={{ background: "var(--mf-muted)" }} data-testid={`pathway-${p.id}`}>
                <div className="text-3xl mb-2">{p.icon}</div>
                <div className="font-serif text-xl mb-1">{p.label}</div>
                <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{p.desc}</div>
              </button>
            ))}
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card testid="merge-sources" delay={0.1}>
          <h2 className="font-serif text-2xl mb-4">Choose source</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {sources.map((s) => (
              <button key={s.id} onClick={() => { setSelectedSource(s); setStep(2); }} className="p-4 rounded-2xl flex flex-col items-center gap-2 transition-all hover:-translate-y-1 relative" style={{ background: "var(--mf-muted)" }} data-testid={`source-${s.id}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold" style={{ background: "var(--mf-primary)" }}>{s.logo}</div>
                <div className="text-sm font-medium text-center">{s.name}</div>
                {s.real_api_pending && <Pill variant="warn">CSV only*</Pill>}
              </button>
            ))}
          </div>
          <div className="text-xs mt-4" style={{ color: "var(--mf-text-muted)" }}>* Direct API/OAuth integration with these vendors is pending — CSV export upload works today.</div>
        </Card>
      )}

      {step === 2 && (
        <Card testid="merge-upload" delay={0.1}>
          <h2 className="font-serif text-2xl mb-4">Upload your export</h2>
          <p className="text-sm mb-3" style={{ color: "var(--mf-text-muted)" }}>Source: <strong>{selectedSource?.name}</strong></p>
          <div className="p-8 rounded-2xl border-2 border-dashed text-center mb-3" style={{ borderColor: "var(--mf-primary)", background: "rgba(26,107,114,0.04)" }}>
            <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--mf-primary)" }} />
            <input ref={fileRef} type="file" accept=".csv" className="text-sm" data-testid="merge-file-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm rounded-full">Back</button>
            <PrimaryButton onClick={upload} testid="merge-upload-btn">Preview Mapping</PrimaryButton>
          </div>
        </Card>
      )}

      {step === 3 && preview && (
        <Card testid="merge-mapping" delay={0.1}>
          <h2 className="font-serif text-2xl mb-4">Field Mapping</h2>
          <p className="text-sm mb-4" style={{ color: "var(--mf-text-muted)" }}>{preview.total_rows} rows detected · estimated {preview.estimated_minutes} min · review and confirm:</p>
          <div className="space-y-2 mb-5">
            {preview.fieldnames.map((f) => (
              <div key={f} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--mf-muted)" }}>
                <div className="text-sm flex-1 font-medium">{f}</div>
                <ArrowRight className="w-4 h-4" style={{ color: "var(--mf-text-muted)" }} />
                <select value={mapping[f] || ""} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value || null })} className="px-3 py-1.5 rounded-lg outline-none text-sm" style={{ background: "var(--mf-surface)" }} data-testid={`map-${f}`}>
                  <option value="">— skip —</option>
                  {preview.target_fields.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="text-xs mb-3" style={{ color: "var(--mf-text-muted)" }}>Sample (first {preview.sample_rows.length} rows):</div>
          <div className="overflow-x-auto mb-5">
            <table className="text-xs w-full">
              <thead><tr>{preview.fieldnames.map(f => <th key={f} className="px-2 py-1 text-left" style={{ color: "var(--mf-text-muted)" }}>{f}</th>)}</tr></thead>
              <tbody>{preview.sample_rows.map((r, i) => <tr key={i}>{preview.fieldnames.map(f => <td key={f} className="px-2 py-1 truncate max-w-[120px]">{r[f]}</td>)}</tr>)}</tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setStep(2)} className="px-4 py-2 text-sm rounded-full">Back</button>
            <PrimaryButton onClick={run} testid="merge-run-btn">Run Migration</PrimaryButton>
          </div>
        </Card>
      )}

      {step === 4 && (
        <Card testid="merge-running">
          <div className="text-center py-8">
            <div className="w-12 h-12 mx-auto mb-4 border-4 rounded-full animate-spin" style={{ borderColor: "var(--mf-muted)", borderTopColor: "var(--mf-primary)" }}></div>
            <div className="font-serif text-2xl mb-1">Importing your records…</div>
            <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>This may take a moment for large files.</div>
          </div>
        </Card>
      )}

      {step === 5 && result && (
        <Card testid="merge-result" delay={0.1}>
          <div className="flex items-start gap-3 mb-5">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "rgba(123,174,127,0.18)" }}>
              <Check className="w-6 h-6" style={{ color: "#4a7a4e" }} />
            </div>
            <div>
              <h2 className="font-serif text-2xl">Migration complete</h2>
              <div className="text-sm" style={{ color: "var(--mf-text-muted)" }}>Rollback available for 30 days</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="p-4 rounded-xl text-center" style={{ background: "var(--mf-muted)" }}><div className="font-serif text-3xl" style={{ color: "var(--mf-primary)" }}>{result.imported}</div><div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Imported</div></div>
            <div className="p-4 rounded-xl text-center" style={{ background: "var(--mf-muted)" }}><div className="font-serif text-3xl">{result.skipped}</div><div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Duplicates skipped</div></div>
            <div className="p-4 rounded-xl text-center" style={{ background: "var(--mf-muted)" }}><div className="font-serif text-3xl" style={{ color: result.flagged ? "#B45253" : undefined }}>{result.flagged}</div><div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Flagged</div></div>
          </div>
          <div className="flex gap-2">
            <PrimaryButton onClick={() => { setStep(0); setPathway(""); setPreview(null); setResult(null); setSelectedSource(null); }} testid="merge-new-btn">New Migration</PrimaryButton>
            <button onClick={() => window.location.href = "/clients"} className="px-4 py-2 text-sm rounded-full" style={{ background: "var(--mf-muted)" }} data-testid="merge-view-clients">View Clients</button>
          </div>
        </Card>
      )}

      {migrations.length > 0 && (
        <div className="mt-8">
          <h2 className="font-serif text-2xl mb-4">Migration History</h2>
          <Card>
            <div className="space-y-2">
              {migrations.map(m => (
                <div key={m.id} className="p-3 rounded-xl flex items-center justify-between" style={{ background: "var(--mf-muted)" }} data-testid={`mig-${m.id}`}>
                  <div>
                    <div className="font-medium text-sm">{m.source} · {m.imported} imported</div>
                    <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Pill variant={m.rolled_back ? "danger" : "success"}>{m.rolled_back ? "Rolled back" : "Active"}</Pill>
                    {!m.rolled_back && new Date(m.rollback_until) > new Date() && (
                      <button onClick={() => rollback(m.id)} className="text-xs px-3 py-1 rounded-full flex items-center gap-1" style={{ background: "rgba(180,82,83,0.1)", color: "#b91c1c" }} data-testid={`rollback-${m.id}`}>
                        <Undo2 className="w-3 h-3" /> Rollback
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </Page>
  );
}
